import React, { useEffect, useRef, useState } from "react";
import "./itemSortDropdown.css";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";

const SORT_OPTIONS = [
  {
    key: "name",
    label: "Item name",
  },
  {
    key: "quantity",
    label: "Quantity",
  },
  {
    key: "price",
    label: "Price",
  },
  {
    key: "updatedAt",
    label: "Updated at",
  },
];

const ItemSortDropdown = ({ itemsData = [], setItemsData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSort, setSelectedSort] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchValue, setSearchValue] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(null);

  const dropdownRef = useRef(null); 
  /* =========================================================
     CLOSE DROPDOWN WHEN CLICKING OUTSIDE
  ========================================================= */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* =========================================================
     GET ITEM QUANTITY
  ========================================================= */

  const getItemQuantity = (item) => {
    if (!Array.isArray(item?.inventories)) {
      return 0;
    }

    return item.inventories.reduce((total, inventory) => {
      return total + Number(inventory?.stock?.quantity || 0);
    }, 0);
  };

  /* =========================================================
     GET SORT VALUE
  ========================================================= */

  const getSortValue = (item, sortKey) => {
    switch (sortKey) {
      case "name":
        return String(item?.name || "")
          .trim()
          .toLowerCase();

      case "quantity":
        return getItemQuantity(item);

      case "price":
        return Number(item?.pricing?.sellingPrice || 0);

      case "updatedAt":
        return new Date(
          item?.updatedAt || item?.createdAt || 0
        ).getTime();

      default:
        return "";
    }
  };

  /* =========================================================
     SORT ITEMS
  ========================================================= */

  const sortItems = (items, sortKey, direction) => {
    return [...items].sort((a, b) => {
      const valueA = getSortValue(a, sortKey);
      const valueB = getSortValue(b, sortKey);

      let comparison = 0;

      if (
        typeof valueA === "string" &&
        typeof valueB === "string"
      ) {
        comparison = valueA.localeCompare(valueB);
      } else {
        comparison = valueA - valueB;
      }

      return direction === "asc"
        ? comparison
        : -comparison;
    });
  };

  /* =========================================================
     HANDLE SORT
  ========================================================= */
const handleSort1 = (sortKey, nextDirection) => {
  return new Promise((resolve) => {
    setItemsData((currentItems) => {
      const sortedItems = sortItems(
        currentItems,
        sortKey,
        nextDirection
      );

      resolve();
      return sortedItems;
    });
  });
};

const handleSort2 = (sortKey, nextDirection) => {
  setSelectedSort(sortKey);
  setSortDirection(nextDirection);
  setIsOpen(false);
};

const handleSort = async (sortKey) => {
  const nextDirection =
    selectedSort === sortKey
      ? sortDirection === "asc"
        ? "desc"
        : "asc"
      : "asc";

  await handleSort1(sortKey, nextDirection);

  handleSort2(sortKey, nextDirection);
};
  /* =========================================================
     SELECTED SORT LABEL
     
     IMPORTANT:
     This is NOT state.

     It is derived from selectedSort during render.
     When selectedSort changes, React renders again and
     this automatically gets the new label.
  ========================================================= */

  const selectedSortLabel =
    SORT_OPTIONS.find(
      (option) => option.key === selectedSort
    )?.label || "Item name";

  /* =========================================================
     FILTER SORT OPTIONS
  ========================================================= */

  const filteredSortOptions = SORT_OPTIONS.filter(
    (option) =>
      option.label
        .toLowerCase()
        .includes(searchValue.toLowerCase())
  );

  return (
    <div
      ref={dropdownRef}
      className="relative item-sort-wrapper"
    >
      {/* =====================================================
          SORT BUTTON
      ===================================================== */}

      <button
        type="button"
        className="item-header-project-button fx fx-ac space"
        onClick={() => setIsOpen((previous) => !previous)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <SwapVertIcon />

        <span>{selectedSortLabel}</span>

        <KeyboardArrowDown
          className={
            isOpen ? "item-sort-arrow-open" : ""
          }
        />
      </button>

      {/* =====================================================
          SORT DROPDOWN
      ===================================================== */}

      {isOpen && (
        <div className="item-sort-dropdown shadowA">
          {/* =================================================
              SEARCH
          ================================================= */}

          <div className="item-sort-search fx fx-ac space">
            <SearchIcon />

            <input
              type="text"
              value={searchValue}
              onChange={(event) =>
                setSearchValue(event.target.value)
              }
              placeholder="Search items..."
            />
          </div>

          {/* =================================================
              SORT OPTIONS
          ================================================= */}

          <div className="item-sort-options fx-cl">
            {filteredSortOptions.map((option, index) => {
              const isSelected =
                selectedSort === option.key;

              return (
                <button
                  key={option.key}
                  type="button"
                  className={`item-sort-option fx fx-ac space ${
                    isSelected
                      ? "item-sort-option-selected"
                      : ""
                  }`}
                  onClick={() =>   handleSort(option.key)
                  }
                >
                  <span>{option.label}</span>

                  {isSelected && (
                    <span className="item-sort-direction">
                      {sortDirection === "asc"
                        ? "↑"
                        : "↓"}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemSortDropdown;